"use client";

import { c as _c } from "react/compiler-runtime";
import { groupReactionNotifications } from "@/components/brain/notifications/utils/groupReactionNotifications";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import { commonApiFetch } from "@/services/api/common-api";
import type { NotificationDisplayItem, TypedNotificationsResponse } from "@/types/feed.types";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
interface UseNotificationsQueryProps {
  /**
   * If true, reverse the notifications order (e.g. for a "descending" / "newest first" display).
   */
  readonly reverse?: boolean | undefined;

  /**
   * Only fetch notifications if we have a valid identity.
   * Used in "enabled" to avoid sending queries prematurely.
   */
  readonly identity?: string | null | undefined;

  /**
   * Example usage where you only fetch if no active profile proxy is set.
   * Adjust or remove according to your own logic.
   */
  readonly activeProfileProxy?: boolean | undefined;

  /**
   * How many notifications to fetch per page.
   */
  readonly limit?: string | undefined;

  /**
   * The cause of the notifications to fetch (include filter).
   */
  readonly cause?: ApiNotificationCause[] | null | undefined;
}
type NotificationsQueryParams = {
  limit: string;
  cause: ApiNotificationCause[] | null;
  pageParam?: number | null | undefined;
  signal?: AbortSignal | undefined;
};
const getIdentityNotificationsQueryKey = (identity: string | null | undefined, limit: string, cause: ApiNotificationCause[] | null) => [QueryKey.IDENTITY_NOTIFICATIONS, {
  identity,
  limit,
  cause: cause?.length ? [...cause].sort((a, b) => a.localeCompare(b)).join(",") : null
}] as const;
const fetchNotifications = async ({
  limit,
  cause,
  pageParam,
  signal
}: NotificationsQueryParams) => {
  const params: Record<string, string> = {
    limit
  };
  if (pageParam != null) {
    params["id_less_than"] = String(pageParam);
  }
  if (cause?.length) {
    params["cause"] = cause.join(",");
  }
  return await commonApiFetch<TypedNotificationsResponse>({
    endpoint: "notifications",
    params,
    signal
  });
};
export function useNotificationsQuery(t0) {
  const $ = _c(36);
  const {
    reverse: t1,
    identity,
    activeProfileProxy: t2,
    limit: t3,
    cause: t4
  } = t0;
  const reverse = t1 === undefined ? false : t1;
  const activeProfileProxy = t2 === undefined ? false : t2;
  const limit = t3 === undefined ? "30" : t3;
  const cause = t4 === undefined ? null : t4;
  const prefetch = usePrefetchNotifications();
  let t5;
  if ($[0] !== identity) {
    t5 = identity?.trim().toLowerCase() ?? null;
    $[0] = identity;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const normalizedIdentity = t5;
  let t6;
  let t7;
  if ($[2] !== activeProfileProxy || $[3] !== cause || $[4] !== identity || $[5] !== limit || $[6] !== prefetch) {
    t6 = () => {
      if (!identity || activeProfileProxy) {
        return;
      }
      prefetch({
        identity,
        limit,
        cause,
        pages: 1
      });
    };
    t7 = [prefetch, identity, activeProfileProxy, limit, cause];
    $[2] = activeProfileProxy;
    $[3] = cause;
    $[4] = identity;
    $[5] = limit;
    $[6] = prefetch;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== cause || $[10] !== identity || $[11] !== limit) {
    t8 = getIdentityNotificationsQueryKey(identity, limit, cause);
    $[9] = cause;
    $[10] = identity;
    $[11] = limit;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== cause || $[14] !== limit) {
    t9 = t10 => {
      const {
        pageParam,
        signal
      } = t10;
      return fetchNotifications({
        limit,
        cause,
        pageParam,
        signal
      });
    };
    $[13] = cause;
    $[14] = limit;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = !!identity && !activeProfileProxy;
  let t11;
  if ($[16] !== normalizedIdentity) {
    t11 = (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[1] as {
        identity?: string | null;
      } | undefined;
      const previousIdentity = typeof previousParams?.identity === "string" ? previousParams.identity.trim().toLowerCase() : null;
      if (previousIdentity === normalizedIdentity) {
        return previousData;
      }
    };
    $[16] = normalizedIdentity;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t8 || $[21] !== t9) {
    t12 = {
      queryKey: t8,
      queryFn: t9,
      initialPageParam: null,
      getNextPageParam: _temp,
      enabled: t10,
      staleTime: 60000,
      placeholderData: t11,
      retry: _temp2
    };
    $[18] = t10;
    $[19] = t11;
    $[20] = t8;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const query = useInfiniteQuery(t12);
  let t13;
  bb0: {
    if (!query.data) {
      let t14;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = [];
        $[23] = t14;
      } else {
        t14 = $[23];
      }
      t13 = t14;
      break bb0;
    }
    const t14 = query.data.pages as TypedNotificationsResponse[];
    let t15;
    if ($[24] !== reverse || $[25] !== t14) {
      const data = t14.flatMap(_temp3);
      t15 = reverse ? [...data].reverse() : data;
      $[24] = reverse;
      $[25] = t14;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t13 = t15;
  }
  const rawItems = t13;
  let t14;
  bb1: {
    if (!query.data) {
      let t15;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = [];
        $[27] = t15;
      } else {
        t15 = $[27];
      }
      t14 = t15;
      break bb1;
    }
    const t15 = query.data.pages as TypedNotificationsResponse[];
    let t16;
    if ($[28] !== reverse || $[29] !== t15) {
      const notifications = t15.flatMap(_temp4);
      const grouped = groupReactionNotifications(notifications);
      t16 = reverse ? [...grouped].reverse() : grouped;
      $[28] = reverse;
      $[29] = t15;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    t14 = t16;
  }
  const items = t14;
  const isInitialQueryDone = query.isSuccess || query.isError;
  let t15;
  if ($[31] !== isInitialQueryDone || $[32] !== items || $[33] !== query || $[34] !== rawItems) {
    t15 = {
      ...query,
      items,
      rawItems,
      isInitialQueryDone
    };
    $[31] = isInitialQueryDone;
    $[32] = items;
    $[33] = query;
    $[34] = rawItems;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}
function _temp4(page_0) {
  return page_0.notifications;
}
function _temp3(page) {
  return page.notifications;
}
function _temp2(failureCount, error) {
  const status = (error as any)?.status ?? (error as any)?.response?.status ?? (error as any)?.cause?.status;
  if (status === 401) {
    return false;
  }
  if (typeof error === "string" && /unauthorized/i.test(error)) {
    return false;
  }
  if (error instanceof Error && /unauthorized/i.test(error.message)) {
    return false;
  }
  return failureCount < 3;
}
function _temp(lastPage) {
  return lastPage.notifications.at(-1)?.id ?? null;
}
export function usePrefetchNotifications() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = t1 => {
      const {
        identity,
        cause: t2,
        limit: t3,
        pages: t4
      } = t1;
      const cause = t2 === undefined ? null : t2;
      const limit = t3 === undefined ? "30" : t3;
      const pages = t4 === undefined ? 3 : t4;
      if (!identity) {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey: getIdentityNotificationsQueryKey(identity, limit, cause?.length ? cause : null),
        queryFn: t5 => {
          const {
            pageParam,
            signal
          } = t5;
          return fetchNotifications({
            limit,
            cause,
            pageParam,
            signal
          });
        },
        initialPageParam: null,
        getNextPageParam: _temp5,
        pages,
        staleTime: 60000
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp5(lastPage) {
  return lastPage.notifications.at(-1)?.id ?? null;
}